import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { TIERS, formatRp } from "@/lib/tiers";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Check, X, ZoomIn, ZoomOut, RotateCw, Download, ExternalLink,
  Shield, FileText, Image as ImageIcon, Clock,
} from "lucide-react";

/**
 * Full-page Admin proof viewer.
 *
 * Route: /admin/payments/:id/proof
 *
 * Hardened auth:
 *  • Component renders nothing until AuthContext has resolved (avoids 401 race).
 *  • Non-admin users are redirected to /login.
 *  • Proof bytes are fetched via /api/files/{file_id} which is admin-gated server-side.
 */
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

export default function AdminProofViewer() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, ready } = useAuth();

  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [deciding, setDeciding] = useState(false);

  // Image controls
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const imgWrapRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/payments/${id}`);
      setPayment(data);
      setNote(data?.admin_note || "");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Failed to load payment";
      toast.error(msg);
      if (e.response?.status === 404) nav("/admin/payments");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  // Gate on auth + admin role
  useEffect(() => {
    if (!ready) return;
    if (user === false) { nav(`/login?next=/admin/payments/${id}/proof`); return; }
    if (user?.role !== "admin") { toast.error("Admin access required."); nav("/"); return; }
    load();
  }, [ready, user, id, nav, load]);

  const decide = async (action) => {
    if (!payment) return;
    setDeciding(true);
    try {
      await api.post(`/admin/payments/${id}/decide`, { action, admin_note: note });
      toast.success(`Payment ${action}d`);
      // Reload to reflect updated status
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setDeciding(false);
    }
  };

  const fileUrlResolved = useMemo(() => fileUrl(payment?.proof_url || ""), [payment]);
  const contentType = payment?.proof_file?.content_type || "";
  const isPdf = contentType === "application/pdf" || /\.pdf$/i.test(payment?.proof_file?.original_filename || "");
  const isImage = contentType.startsWith("image/") || (!isPdf && !!fileUrlResolved);

  // Keyboard shortcuts for the image viewer
  useEffect(() => {
    if (!isImage) return;
    const onKey = (e) => {
      if (e.key === "+" || e.key === "=") { e.preventDefault(); setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2))); }
      else if (e.key === "-") { e.preventDefault(); setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2))); }
      else if (e.key === "0") { e.preventDefault(); setZoom(1); setRotation(0); }
      else if (e.key.toLowerCase() === "r") { e.preventDefault(); setRotation((r) => (r + 90) % 360); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isImage]);

  if (!ready || loading) {
    return (
      <PageShell>
        <div className="max-w-7xl mx-auto px-6 py-10 space-y-4">
          <Skeleton className="h-8 w-64 bg-white/5" />
          <Skeleton className="h-96 w-full bg-white/5" />
        </div>
      </PageShell>
    );
  }

  if (!payment) {
    return (
      <PageShell>
        <div className="max-w-3xl mx-auto px-6 py-20 text-center text-zinc-400">Payment not found.</div>
      </PageShell>
    );
  }

  const status = payment.status;
  const statusChip =
    status === "approved"
      ? "bg-emerald-900/40 text-emerald-300 border border-emerald-500/30"
      : status === "rejected"
      ? "bg-rose-900/40 text-rose-300 border border-rose-500/30"
      : "bg-amber-900/40 text-amber-300 border border-amber-500/30";

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-6 py-6">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-5">
          <Button variant="ghost" onClick={() => nav("/admin/payments")} className="text-zinc-300 hover:text-white" data-testid="back-to-payments">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Payments
          </Button>
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <Shield className="w-3 h-3 text-emerald-400" />
            <span>Admin-only viewer · file streamed through backend</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT — viewer */}
          <div className="lg:col-span-2">
            <div className="glass rounded-xl overflow-hidden">
              <div className="flex items-center justify-between p-3 border-b border-white/10 bg-black/40">
                <div className="flex items-center gap-2">
                  {isPdf ? <FileText className="w-4 h-4 text-rose-300" /> : <ImageIcon className="w-4 h-4 text-rose-300" />}
                  <span className="text-sm text-zinc-200 font-medium truncate max-w-xs">
                    {payment.proof_file?.original_filename || "proof"}
                  </span>
                  {payment.proof_file?.size && (
                    <Badge className="bg-zinc-800 text-zinc-400 border border-white/10 ml-2">
                      {(payment.proof_file.size / 1024).toFixed(1)} KB
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {isImage && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)))} data-testid="zoom-out-btn">
                        <ZoomOut className="w-4 h-4" />
                      </Button>
                      <span className="text-xs text-zinc-300 px-2 w-14 text-center font-mono">{Math.round(zoom * 100)}%</span>
                      <Button size="sm" variant="ghost" onClick={() => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)))} data-testid="zoom-in-btn">
                        <ZoomIn className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setRotation((r) => (r + 90) % 360)} data-testid="rotate-btn">
                        <RotateCw className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setZoom(1); setRotation(0); }} className="text-xs">
                        Reset
                      </Button>
                    </>
                  )}
                  <a href={fileUrlResolved} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="ghost"><ExternalLink className="w-4 h-4" /></Button>
                  </a>
                </div>
              </div>

              <div ref={imgWrapRef} className="relative bg-[radial-gradient(circle_at_center,#1c1c1f_0%,#0a0a0c_100%)] min-h-[60vh] max-h-[80vh] overflow-auto grid place-items-center p-6">
                {isImage ? (
                  <img
                    src={fileUrlResolved}
                    alt="payment proof"
                    style={{ transform: `scale(${zoom}) rotate(${rotation}deg)`, transition: "transform 120ms ease-out", maxWidth: "100%" }}
                    className="block shadow-2xl rounded"
                    draggable={false}
                    data-testid="proof-image"
                  />
                ) : isPdf ? (
                  <iframe
                    title="proof-pdf"
                    src={fileUrlResolved}
                    className="w-full h-[70vh] bg-white rounded border border-white/10"
                    data-testid="proof-pdf"
                  />
                ) : (
                  <div className="text-center text-zinc-400">
                    <div className="mb-3">Preview not available for this file type.</div>
                    <a href={fileUrlResolved} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline"><Download className="w-4 h-4 mr-2" />Open File</Button>
                    </a>
                  </div>
                )}
              </div>

              {isImage && (
                <div className="border-t border-white/10 px-4 py-2 text-[11px] text-zinc-500 flex justify-between">
                  <span>Shortcuts: <kbd className="kbd">+</kbd> zoom in · <kbd className="kbd">-</kbd> zoom out · <kbd className="kbd">R</kbd> rotate · <kbd className="kbd">0</kbd> reset</span>
                  <span className="text-emerald-400">file streamed through admin proxy</span>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — details + actions */}
          <aside className="space-y-4">
            <div className="glass rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-lg font-bold text-white">Payment Details</h2>
                <Badge className={statusChip}>{status}</Badge>
              </div>
              <dl className="space-y-2.5 text-sm">
                <Field label="User" value={`${payment.user_name || "—"}`} sub={payment.user_email} />
                <Field label="Package" value={<Badge className={TIERS[payment.tier]?.chip}>{TIERS[payment.tier]?.label || payment.tier}</Badge>} />
                <Field label="Method" value={(payment.method || "").toUpperCase()} />
                <Field label="Amount" value={<span className="font-mono text-emerald-300">{formatRp(payment.amount)}</span>} />
                <Field label="Sender Name" value={payment.sender_name || "—"} />
                <Field label="Sender Account" value={payment.sender_account || "—"} />
                <Field label="User Note" value={payment.note || "—"} />
                <Field
                  label="Submitted"
                  value={<span className="flex items-center gap-1 text-zinc-300"><Clock className="w-3 h-3 text-zinc-500" />{new Date(payment.created_at).toLocaleString()}</span>}
                />
                {payment.decided_at && (
                  <Field label="Decided" value={new Date(payment.decided_at).toLocaleString()} />
                )}
              </dl>
            </div>

            <div className="glass rounded-xl p-5">
              <label className="text-xs text-zinc-400 uppercase tracking-wider">Admin Note</label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                placeholder="Optional message shown to the user (rejection reason, etc.)"
                className="mt-1 bg-black/40 border-white/10"
                disabled={status !== "pending"}
                data-testid="admin-proof-note"
              />
            </div>

            {status === "pending" ? (
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={() => decide("approve")}
                  disabled={deciding}
                  className="bg-emerald-600 hover:bg-emerald-500 font-semibold shadow-lg shadow-emerald-900/40"
                  data-testid="approve-proof-btn"
                >
                  <Check className="w-4 h-4 mr-2" /> Approve
                </Button>
                <Button
                  onClick={() => decide("reject")}
                  disabled={deciding}
                  className="bg-rose-600 hover:bg-rose-500 font-semibold shadow-lg shadow-rose-900/40"
                  data-testid="reject-proof-btn"
                >
                  <X className="w-4 h-4 mr-2" /> Reject
                </Button>
              </div>
            ) : (
              <div className="text-center text-xs text-zinc-500 italic">
                This payment has already been {status}. Decisions cannot be changed.
              </div>
            )}
          </aside>
        </div>
      </section>
    </PageShell>
  );
}

function Field({ label, value, sub }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</dt>
      <dd className="text-white mt-0.5">{value}</dd>
      {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}
